import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SortableList from "@/components/admin/SortableList";
import MediaDropzone from "@/components/admin/MediaDropzone";

const EMPTY = {
  image_url: "", public_id: "",
  title: "", wedding_name: "", description: "",
  location: "", category: "", order: 0,
};

const CATEGORIES = ["Destination", "Luxury", "Traditional", "Modern", "Outdoor", "Intimate"];

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    return api.get("/gallery").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (item) => {
    try {
      if (item.id) {
        const { data } = await api.patch(`/gallery/${item.id}`, item);
        setItems((all) => all.map((i) => (i.id === data.id ? data : i)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        toast.success("Image updated");
      } else {
        const { data } = await api.post("/gallery", { ...item, order: items.length + 1 });
        setItems((all) => [...all, data]);
        toast.success("Image added");
      }
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      setItems((all) => all.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const reorder = async (ids, next) => {
    setItems(next);
    try { await api.post("/gallery/reorder", { ids }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div data-testid="admin-gallery-page">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Gallery</div>
          <h1 className="font-display text-3xl md:text-4xl text-charcoal">Wedding photographs</h1>
          <p className="text-charcoal/65 font-light mt-2">Drag to reorder · upload images directly.</p>
        </div>
        <button type="button" onClick={() => setEditing({ ...EMPTY })} data-testid="gallery-add-btn" className="btn-primary h-11 px-5 text-[11px]">
          <Plus className="h-4 w-4" /> Add image
        </button>
      </header>

      {loading ? (
        <div className="text-charcoal/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-charcoal/40 italic">No gallery images yet. Click &ldquo;Add image&rdquo;.</div>
      ) : (
        <SortableList
          items={items}
          onReorder={reorder}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          renderItem={(it, Handle) => (
            <div data-testid={`gallery-item-${it.id}`} className="group relative bg-white border border-beige">
              <div className="absolute top-2 left-2 z-10">{Handle}</div>
              <div className="aspect-[4/5] overflow-hidden">
                {it.image_url ? (
                  <img src={it.image_url} alt={it.title || "Gallery"} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-charcoal/40 text-sm">No image</div>
                )}
              </div>
              <div className="p-3">
                <div className="font-display text-base text-charcoal truncate">{it.wedding_name || it.title || "Untitled"}</div>
                <div className="text-xs text-charcoal/55 truncate">{it.location || "—"} · {it.category || "—"}</div>
              </div>
              <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => setEditing(it)} className="h-8 w-8 bg-ivory border border-beige flex items-center justify-center text-charcoal hover:bg-teal-600 hover:text-ivory">
                  <Save className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => remove(it.id)} className="h-8 w-8 bg-ivory border border-beige flex items-center justify-center text-rose-700 hover:bg-rose-700 hover:text-ivory">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        />
      )}

      {editing ? <EditModal item={editing} onClose={() => setEditing(null)} onSave={save} /> : null}
    </div>
  );
}

function EditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({ ...item });
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="gallery-edit-modal">
      <div className="absolute inset-0 bg-charcoal/45" onClick={onClose} />
      <div className="relative bg-ivory border border-beige w-full max-w-xl p-7 max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 h-9 w-9 border border-beige hover:bg-charcoal hover:text-ivory inline-flex items-center justify-center">
          <X className="h-4 w-4" />
        </button>
        <div className="font-display text-2xl text-charcoal mb-5">{form.id ? "Edit gallery item" : "Add gallery item"}</div>

        <div className="mb-5">
          <MediaDropzone
            folder="mostash/gallery"
            accept="image"
            height="aspect-[4/5]"
            label="Image *"
            testid="gallery-image-dropzone"
            value={form.image_url ? { url: form.image_url, public_id: form.public_id, resource_type: "image" } : null}
            onChange={(asset) => setForm((f) => ({ ...f, image_url: asset?.url || "", public_id: asset?.public_id || "" }))}
          />
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Wedding name</span>
            <input data-testid="gallery-wedding-name" name="wedding_name" value={form.wedding_name || ""} onChange={onChange} className="mt-2 field-input" placeholder="e.g. Aanya & Ishaan" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Location</span>
              <input name="location" value={form.location || ""} onChange={onChange} className="mt-2 field-input" placeholder="Udaipur · India" />
            </label>
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Category</span>
              <select name="category" value={form.category || ""} onChange={onChange} className="mt-2 field-input bg-white">
                <option value="">Select…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Description</span>
            <textarea name="description" rows={4} value={form.description || ""} onChange={onChange} className="mt-2 field-textarea" placeholder="A short story about this wedding…" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Title (optional caption)</span>
            <input name="title" value={form.title || ""} onChange={onChange} className="mt-2 field-input" />
          </label>
        </div>

        <div className="mt-7 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary h-10 px-5 text-[11px]">Cancel</button>
          <button type="button" onClick={() => onSave(form)} data-testid="gallery-edit-save" className="btn-primary h-10 px-5 text-[11px]">Save</button>
        </div>
      </div>
    </div>
  );
}
